import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { setupPage } from 'csstips/lib/page';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import './i18n';
import { Tooltip } from 'react-tooltip';
import { style } from 'typestyle';
import { KeycloakProvider } from './features/authentication/keycloak/KeycloakProvider';
import App from './core/app.component';
import * as serviceWorker from './serviceWorker';
import * as styles from './features/offices/offices.styles';
import AppConstants from './core/app.constants';
import RouteConstants from './core/routes.constants';
import DisplayScreen from './features/statistics/dashboard/screen/display-screen.component';
import KioskErrorBoundary from './features/statistics/dashboard/screen/kiosk-error-boundary.component';
import FacilityDisplayScreen from './features/facilities/screen/facility-display-screen.component';

setupPage('#root');

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate: 1.0,
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
const rootElement = document.getElementById('root');

// The kiosk/TV display screens are the portal's only unauthenticated routes. They're matched here,
// before KeycloakProvider mounts, so visiting one never triggers a login-required redirect
// (`onLoad: 'login-required'`) — each screen authenticates itself with a paired X-Display-Token
// instead of a Keycloak session.
// Matched on the whole first path segment, not as a prefix: a startsWith() check would also match a
// future route like /schermen or /schermafbeelding and render the unauthenticated screen for it,
// skipping Keycloak entirely.
const firstPathSegment = window.location.pathname.replace(/^\/+/, '').split('/')[0];

// Nobody is standing at a lobby TV to press reload, so a render-time throw must not be terminal:
// the boundary re-mounts the screen instead of leaving it blank.
const kioskScreens: Record<string, () => React.JSX.Element> = {
  [RouteConstants.PATH_DISPLAY_SCREEN]: () => (
    <KioskErrorBoundary>
      <DisplayScreen />
    </KioskErrorBoundary>
  ),
  [RouteConstants.PATH_FACILITY_DISPLAY_SCREEN]: () => (
    <KioskErrorBoundary>
      <FacilityDisplayScreen />
    </KioskErrorBoundary>
  ),
};

// A lookup rather than a chain of ternaries: each additional kiosk route is one more entry, and the
// authenticated shell stays a single unambiguous fallback instead of the tail of nested conditionals.
const renderKioskScreen = kioskScreens[firstPathSegment];

if (rootElement !== null) {
  createRoot(rootElement).render(
    renderKioskScreen ? (
      <StrictMode>{renderKioskScreen()}</StrictMode>
    ) : (
      <BrowserRouter>
        <KeycloakProvider>
          <StrictMode>
            <App />
            <Tooltip
              id={AppConstants.TOOLTIP_GENERAL_ID}
              render={({ content }): JSX.Element => <span>{content}</span>}
              className={style(styles.tooltip)}
            />
          </StrictMode>
        </KeycloakProvider>
      </BrowserRouter>
    ),
  );
}
